import React, { useState, useEffect, useRef } from 'react'
import { MapPin, Calendar, Clock, AlertCircle, Sparkles, Loader2 } from 'lucide-react'
import { EventFormData } from '../EventCreationWizard'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../../ui/EnterpriseToast'

interface LocationScheduleStepProps {
  formData: EventFormData
  updateFormData: (updates: Partial<EventFormData>) => void
  onNext: () => void
  onPrev: () => void
  isFirstStep: boolean
  isLastStep: boolean
}

interface DateValidation {
  notification_before_registration: boolean
  registration_before_event: boolean
  closes_before_event: boolean
  pickup_during_or_after_event: boolean
}

const LocationScheduleStep: React.FC<LocationScheduleStepProps> = ({
  formData,
  updateFormData,
  onNext,
  onPrev
}) => {
  const addressInputRef = useRef<HTMLInputElement>(null)
  const [validation, setValidation] = useState<DateValidation>({
    notification_before_registration: true,
    registration_before_event: true,
    closes_before_event: true,
    pickup_during_or_after_event: true
  })

  // Natural language scheduling state
  const [nlDescription, setNlDescription] = useState('')
  const [isParsing, setIsParsing] = useState(false)
  const [parseError, setParseError] = useState('')

  // Auto-populate location_name from event title when component mounts
  useEffect(() => {
    if (!formData.location_name && formData.title) {
      console.log('[LocationScheduleStep] Auto-populating location_name from title:', formData.title)
      updateFormData({ location_name: formData.title })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Initialize Google Places Autocomplete
  useEffect(() => {
    const initAutocomplete = async () => {
      const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY

      if (!apiKey) {
        console.warn('Google Places API key not found')
        return
      }

      if (!addressInputRef.current) return

      try {
        // Load Google Maps script if not already loaded
        if (!window.google) {
          const script = document.createElement('script')
          script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=Function.prototype`
          script.async = true
          script.defer = true
          document.head.appendChild(script)

          await new Promise((resolve) => {
            script.onload = resolve
          })
        }

        const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
          fields: ['address_components', 'formatted_address', 'geometry', 'name'],
          types: ['address']
        })

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace()

          if (!place.geometry || !place.geometry.location) {
            toast.custom((t) => (
              <EnterpriseToast
                t={t}
                type="error"
                title="Location Data Unavailable"
                message="No location data available for this address"
              />
            ))
            return
          }

          // Extract address components
          let zipCode = ''
          let streetNumber = ''
          let route = ''
          let city = ''
          let state = ''

          place.address_components?.forEach(component => {
            const types = component.types
            if (types.includes('postal_code')) {
              zipCode = component.long_name
            }
            if (types.includes('street_number')) {
              streetNumber = component.long_name
            }
            if (types.includes('route')) {
              route = component.long_name
            }
            if (types.includes('locality')) {
              city = component.long_name
            }
            if (types.includes('administrative_area_level_1')) {
              state = component.short_name
            }
          })

          const formattedAddress = place.formatted_address ||
            `${streetNumber} ${route}, ${city}, ${state}`.trim()

          // Update form with place data
          updateFormData({
            address: formattedAddress,
            zip_code: zipCode,
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng()
          })

          toast.custom((t) => (
            <EnterpriseToast
              t={t}
              type="success"
              title="Address Populated"
              message="Address details have been auto-filled successfully"
            />
          ))
        })
      } catch (error) {
        console.error('Failed to initialize Google Places:', error)
      }
    }

    initAutocomplete()
  }, [])

  // Validate dates whenever they change
  useEffect(() => {
    validateDates()
  }, [
    formData.notification_date,
    formData.registration_opens,
    formData.registration_closes,
    formData.event_date,
    formData.pickup_start_time,
    formData.pickup_end_time
  ])

  const validateDates = () => {
    // All fields now store ISO datetime strings, parse them consistently
    const notificationDate = formData.notification_date ? new Date(formData.notification_date) : null
    const registrationOpens = formData.registration_opens ? new Date(formData.registration_opens) : null
    const registrationCloses = formData.registration_closes ? new Date(formData.registration_closes) : null
    const eventDate = formData.event_date ? new Date(formData.event_date) : null
    const pickupStart = formData.pickup_start_time ? new Date(formData.pickup_start_time) : null
    const pickupEnd = formData.pickup_end_time ? new Date(formData.pickup_end_time) : null

    const newValidation: DateValidation = {
      notification_before_registration: !notificationDate || !registrationOpens || notificationDate <= registrationOpens,
      registration_before_event: !registrationOpens || !eventDate || registrationOpens < eventDate,
      closes_before_event: !registrationCloses || !eventDate || registrationCloses <= eventDate,
      pickup_during_or_after_event: !pickupStart || !eventDate || pickupStart >= eventDate
    }

    setValidation(newValidation)
  }

  const hasValidationErrors = () => {
    return !validation.notification_before_registration ||
           !validation.registration_before_event ||
           !validation.closes_before_event ||
           !validation.pickup_during_or_after_event
  }

  // Handle AI-powered auto-fill
  const handleAutoFill = async () => {
    if (!nlDescription.trim()) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Description Required"
          message="Please enter a description of your event schedule"
        />
      ))
      return
    }

    setIsParsing(true)
    setParseError('')

    try {
      // Detect if running on Netlify Dev (port 8888) or regular Vite dev
      const isNetlifyDev = window.location.port === '8888'
      const apiUrl = isNetlifyDev
        ? '/.netlify/functions/parse-schedule' // Local function on Netlify Dev
        : import.meta.env.DEV
        ? 'https://secret.tailtations.com/.netlify/functions/parse-schedule' // Production API on regular dev
        : '/.netlify/functions/parse-schedule' // Production

      // Get user's local date/time (not UTC)
      const now = new Date()
      const userLocalDateTime = now.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      })

      console.log('[AI Scheduling] Sending to API:', {
        description: nlDescription,
        currentDate: userLocalDateTime,
        apiUrl
      })

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: nlDescription,
          currentDate: userLocalDateTime
        })
      })

      if (!response.ok) {
        const error = await response.json()
        console.error('[AI Scheduling] API Error:', error)
        throw new Error(error.message || 'Failed to parse schedule')
      }

      const parsed = await response.json()
      console.log('[AI Scheduling] Received from API:', parsed)

      // Auto-fill form fields with parsed data
      const updates: Partial<EventFormData> = {}

      if (parsed.eventStart) updates.event_date = parsed.eventStart
      if (parsed.registrationOpen) updates.registration_opens = parsed.registrationOpen
      if (parsed.registrationClose) updates.registration_closes = parsed.registrationClose
      if (parsed.pickupStart) updates.pickup_start_time = parsed.pickupStart
      if (parsed.pickupEnd) updates.pickup_end_time = parsed.pickupEnd
      if (parsed.notificationTime) updates.notification_date = parsed.notificationTime

      console.log('[AI Scheduling] Form updates:', updates)

      updateFormData(updates)

      // Show success message
      if (parsed.confidence === 'low') {
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="success"
            title="Schedule Auto-Filled"
            message="Please review carefully - some dates may need adjustment."
          />
        ))
      } else {
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="success"
            title="Schedule Auto-Filled"
            message="Please review the dates below."
          />
        ))
      }

      // Clear the input
      setNlDescription('')

    } catch (error: any) {
      console.error('Parse error:', error)
      setParseError(error.message || 'Could not parse schedule. Please try rephrasing or fill manually.')
    } finally {
      setIsParsing(false)
    }
  }

  const handleNext = () => {
    // Validate required fields
    if (!formData.location_name.trim()) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Location Name Required"
          message="Please enter a location name"
        />
      ))
      return
    }

    if (!formData.address.trim()) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Address Required"
          message="Please enter a street address"
        />
      ))
      return
    }

    if (!formData.zip_code.trim()) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Zip Code Required"
          message="Please enter a zip code"
        />
      ))
      return
    }

    if (!formData.event_date) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Event Date Required"
          message="Please select an event date and time"
        />
      ))
      return
    }

    if (!formData.registration_opens) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Registration Opens Required"
          message="Please select when registration opens"
        />
      ))
      return
    }

    if (!formData.registration_closes) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Registration Closes Required"
          message="Please select when registration closes"
        />
      ))
      return
    }

    if (!formData.pickup_start_time) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Pickup Start Required"
          message="Please select a pickup start time"
        />
      ))
      return
    }

    if (!formData.pickup_end_time) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Pickup End Required"
          message="Please select a pickup end time"
        />
      ))
      return
    }

    // Check for validation errors
    if (hasValidationErrors()) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Validation Errors"
          message="Please fix date/time validation errors before continuing"
        />
      ))
      return
    }

    // Check pickup window makes sense
    const pickupStart = new Date(formData.pickup_start_time)
    const pickupEnd = new Date(formData.pickup_end_time)
    if (pickupEnd <= pickupStart) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Invalid Pickup Window"
          message="Pickup end time must be after pickup start time"
        />
      ))
      return
    }

    // Check registration window makes sense
    const registrationOpens = new Date(formData.registration_opens)
    const registrationCloses = new Date(formData.registration_closes)
    if (registrationCloses <= registrationOpens) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Invalid Registration Window"
          message="Registration must close after it opens"
        />
      ))
      return
    }

    onNext()
  }

  // Helper to format datetime-local input value
  const formatDateTimeLocal = (dateString: string) => {
    if (!dateString) return ''

    console.log('[LocationScheduleStep] formatDateTimeLocal input:', dateString)

    // Parse the date string and convert to local time
    const date = new Date(dateString)

    // Check if valid date
    if (isNaN(date.getTime())) {
      console.warn('[LocationScheduleStep] Invalid date:', dateString)
      return ''
    }

    // Format as YYYY-MM-DDTHH:MM for datetime-local input
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    const formatted = `${year}-${month}-${day}T${hours}:${minutes}`
    console.log('[LocationScheduleStep] formatDateTimeLocal output:', formatted, '(from', dateString, ')')

    return formatted
  }

  // Helper to format date input value
  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    // If already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString
    }
    // Otherwise parse and format
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Helper to parse datetime from input
  const parseDateTimeFromInput = (dateTimeString: string) => {
    if (!dateTimeString) return ''

    // Get the browser's timezone offset in minutes
    const date = new Date(dateTimeString)
    const offsetMinutes = -date.getTimezoneOffset() // Negative because getTimezoneOffset returns opposite sign

    // Convert offset to ±HH:MM format
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60)
    const offsetMins = Math.abs(offsetMinutes) % 60
    const offsetSign = offsetMinutes >= 0 ? '+' : '-'
    const offsetString = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`

    // Return ISO format with timezone offset
    // This tells PostgreSQL the exact timezone the admin is in
    return `${dateTimeString}:00${offsetString}`
  }

  // Helper to display datetime in readable format
  const displayDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return ''
    const date = new Date(dateTimeString)
    return date.toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  // Get min date for various fields (no past dates)
  const today = new Date().toISOString().split('T')[0]

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Location & Schedule</h2>
        <p className="text-muted-foreground">
          Set where and when your event will take place
        </p>
      </div>

      {/* Location Section */}
      <div className="bg-muted/30 border border-border rounded-lg p-6 space-y-4">
        <div className="flex items-center space-x-2 mb-4">
          <MapPin className="text-primary" size={20} />
          <h3 className="text-lg font-semibold text-foreground">Event Location</h3>
        </div>

        {/* Location Name */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Location Name <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={formData.location_name}
            onChange={(e) => updateFormData({ location_name: e.target.value })}
            placeholder="e.g., Downtown LA Pop-Up Kitchen"
            className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-muted-foreground mt-1">
            A friendly name for the location (e.g., restaurant name, neighborhood)
          </p>
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Street Address <span className="text-destructive">*</span>
          </label>
          <input
            ref={addressInputRef}
            type="text"
            value={formData.address}
            onChange={(e) => updateFormData({ address: e.target.value })}
            placeholder="Start typing to search for an address..."
            className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Start typing and select from suggestions to auto-populate address details
          </p>
        </div>

        {/* Zip Code */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Zip Code <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={formData.zip_code}
            onChange={(e) => updateFormData({ zip_code: e.target.value })}
            placeholder="e.g., 90001"
            maxLength={10}
            className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Latitude/Longitude (Optional) */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Latitude <span className="text-muted-foreground">(Optional)</span>
            </label>
            <input
              type="number"
              step="0.000001"
              value={formData.latitude || ''}
              onChange={(e) => updateFormData({ latitude: parseFloat(e.target.value) || null })}
              placeholder="e.g., 34.0522"
              className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Longitude <span className="text-muted-foreground">(Optional)</span>
            </label>
            <input
              type="number"
              step="0.000001"
              value={formData.longitude || ''}
              onChange={(e) => updateFormData({ longitude: parseFloat(e.target.value) || null })}
              placeholder="e.g., -118.2437"
              className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Coordinates will be used to show the event on a map. Leave blank if unsure.
        </p>
      </div>

      {/* Schedule Section */}
      <div className="bg-muted/30 border border-border rounded-lg p-6 space-y-4">
        <div className="flex items-center space-x-2 mb-4">
          <Calendar className="text-primary" size={20} />
          <h3 className="text-lg font-semibold text-foreground">Event Timeline</h3>
        </div>

        {/* Natural Language Input */}
        <div className="rounded-lg border-2 border-dashed border-primary/50 bg-primary/10 p-6 mb-4">
          <div className="flex items-start gap-3 mb-3">
            <Sparkles className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="menu-item-name font-semibold mb-1">
                Quick Schedule with AI
              </h4>
              <p className="text-sm text-gray-600">
                Describe your event schedule in plain English and we'll fill the fields below automatically.
              </p>
            </div>
          </div>

          <textarea
            value={nlDescription}
            onChange={(e) => setNlDescription(e.target.value)}
            placeholder='Example: "My event starts at 10pm today and goes until midnight. Registration opens at 5pm and closes at 9pm."'
            className="w-full min-h-[100px] px-4 py-3 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none mb-3 placeholder:text-gray-500"
            style={{ color: '#111827' }}
          />

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleAutoFill}
              disabled={!nlDescription.trim() || isParsing}
              className="flex items-center gap-2 px-6 py-2 rounded-md font-medium transition-colors bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isParsing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Parsing...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Auto-Fill from Description
                </>
              )}
            </button>

            {parseError && (
              <p className="text-sm text-red-600">{parseError}</p>
            )}
          </div>
        </div>

        {/* Visual Timeline */}
        {formData.notification_date && formData.registration_opens && formData.registration_closes && formData.event_date && (
          <div className="bg-card border border-border rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-foreground mb-3">Timeline Preview</h4>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${validation.notification_before_registration ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm text-foreground">
                  {displayDateTime(formData.notification_date)} - Notification Sent
                </span>
              </div>
              <div className="ml-1.5 border-l-2 border-muted-foreground/30 h-4" />
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${validation.registration_before_event ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm text-foreground">
                  {displayDateTime(formData.registration_opens)} - Registration Opens
                </span>
              </div>
              <div className="ml-1.5 border-l-2 border-muted-foreground/30 h-4" />
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${validation.closes_before_event ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm text-foreground">
                  {displayDateTime(formData.registration_closes)} - Registration Closes
                </span>
              </div>
              <div className="ml-1.5 border-l-2 border-muted-foreground/30 h-4" />
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${validation.pickup_during_or_after_event ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm text-foreground">
                  {displayDateTime(formData.event_date)} - Event Start
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Notification Date & Time */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Notification Date & Time <span className="text-muted-foreground">(Optional)</span>
          </label>
          <input
            type="datetime-local"
            value={formatDateTimeLocal(formData.notification_date)}
            onChange={(e) => updateFormData({ notification_date: parseDateTimeFromInput(e.target.value) })}
            min={today}
            className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {!validation.notification_before_registration && formData.notification_date && (
            <div className="flex items-center space-x-1 mt-1 text-destructive">
              <AlertCircle size={14} />
              <span className="text-xs">Notification should be sent before registration opens</span>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            When to notify SECRET members about this event
          </p>
        </div>

        {/* Registration Opens */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Registration Opens <span className="text-destructive">*</span>
          </label>
          <input
            type="datetime-local"
            value={formatDateTimeLocal(formData.registration_opens)}
            onChange={(e) => updateFormData({ registration_opens: parseDateTimeFromInput(e.target.value) })}
            min={today}
            className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {!validation.registration_before_event && formData.registration_opens && (
            <div className="flex items-center space-x-1 mt-1 text-destructive">
              <AlertCircle size={14} />
              <span className="text-xs">Registration must open before the event starts</span>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            When customers can start placing pre-orders
          </p>
        </div>

        {/* Registration Closes */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Registration Closes <span className="text-destructive">*</span>
          </label>
          <input
            type="datetime-local"
            value={formatDateTimeLocal(formData.registration_closes)}
            onChange={(e) => updateFormData({ registration_closes: parseDateTimeFromInput(e.target.value) })}
            min={formData.registration_opens || today}
            className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {!validation.closes_before_event && formData.registration_closes && (
            <div className="flex items-center space-x-1 mt-1 text-destructive">
              <AlertCircle size={14} />
              <span className="text-xs">Registration should close before the event starts</span>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Last moment customers can place pre-orders
          </p>
        </div>

        {/* Event Date & Time */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Event Start Date & Time <span className="text-destructive">*</span>
          </label>
          <input
            type="datetime-local"
            value={formatDateTimeLocal(formData.event_date)}
            onChange={(e) => updateFormData({ event_date: parseDateTimeFromInput(e.target.value) })}
            min={today}
            className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-muted-foreground mt-1">
            When the event starts (when customers can begin picking up orders)
          </p>
        </div>

        {/* Pickup Window */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <Clock className="text-primary" size={16} />
            <h4 className="text-sm font-semibold text-foreground">Pickup Window</h4>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Pickup Start <span className="text-destructive">*</span>
              </label>
              <input
                type="datetime-local"
                value={formatDateTimeLocal(formData.pickup_start_time)}
                onChange={(e) => updateFormData({ pickup_start_time: parseDateTimeFromInput(e.target.value) })}
                min={formData.event_date || today}
                className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Pickup End <span className="text-destructive">*</span>
              </label>
              <input
                type="datetime-local"
                value={formatDateTimeLocal(formData.pickup_end_time)}
                onChange={(e) => updateFormData({ pickup_end_time: parseDateTimeFromInput(e.target.value) })}
                min={formData.pickup_start_time || formData.event_date || today}
                className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          {!validation.pickup_during_or_after_event && formData.pickup_start_time && (
            <div className="flex items-center space-x-1 mt-2 text-destructive">
              <AlertCircle size={14} />
              <span className="text-xs">Pickup should start on or after the event start time</span>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            The time window when customers can pick up their orders
          </p>
        </div>
      </div>

      {/* Validation Summary */}
      {hasValidationErrors() && (
        <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="text-destructive flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="text-sm font-semibold text-destructive mb-1">Timeline Validation Errors</h4>
              <ul className="text-xs text-destructive space-y-1">
                {!validation.notification_before_registration && <li>• Notification time must be before or at registration open time</li>}
                {!validation.registration_before_event && <li>• Registration must open before the event starts</li>}
                {!validation.closes_before_event && <li>• Registration must close before or at the event start time</li>}
                {!validation.pickup_during_or_after_event && <li>• Pickup window must start on or after the event start time</li>}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LocationScheduleStep
